import axios from 'axios';
import { API } from '../../../backend';
import { useAppContext } from '../../../hooks/context/AppContext';
import { useState, useMemo } from 'react';
import useSilentIdTokenRefresh from "../../../msal/useSilentTokenRefresh";
import { toast } from 'react-toastify';

// Common error handler
const handleError = (error: unknown, context: string) => {
  console.error(`Error ${context}:`, error);
  return null;
};

// Define API Response Type
type APIResponse = {
  currentPage: number;
  totalPages: number;
  totalProjects: number;
  perPage: number;
  projects: Project[];
};

export interface Project {
  project_key: string;
  Project_Id: string;
  bu: string;
  name: string;
  sclead: string;
  country: string;
  category: string;
  subCategory: string;
  brand: string;
  Launch_Year: string;
  launch_Month: string;
  launch_Week: string;
  ProjectStatus: string;
  bigBet: string;
  Bigbetvalue: string;
  uploadPic: string;
  Risk: string;
  comment: string;
  totalProjects: number;
  issaved: number;
  upload_pic?: string;
  country_key: string;
  category_Key: string;
  subcategory_key: string;
  brand_key: string;
  buid: number;
  sclead_key: number;
  risk_key: number;
  big_bet: number;
  Time_Key: number;
  project_name?: string;
  L2_Unilever_Cluster_Desc?: string;
  projectName?: string;
  project_id?: string;
  imageName?: string;
}

interface ProjectCounts {
  projectActive: number;
  projectInactive: number;
  totalProject: number;
}

interface HCGlobalFilters {
  buid: number[];
  countryKey: number[];
  categoryKey: number[];
  subCategoryKey: number[];
  brandKey: number[];
}

interface HCGlobalFiltersResponse {
  data: {
    bu: { key: number; value: string }[];
    country: { key: number; value: string }[];
    category: { key: number; value: string }[];
    subCategory: { key: number; value: string }[];
    brand: { key: number; value: string }[];
  };
}

// Custom hook for Project API client
export const useProjectApiClient = () => {
  const { authData, updateError } = useAppContext();
  const [idToken, setIdToken] = useState(null);
  const getIdTokenSilently = useSilentIdTokenRefresh((token: any | null, account: any | null) => {
    setIdToken(token);
  });

  const apiClient = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken ? `Bearer ${idToken}` : `Bearer ${authData.idToken}`,
      },
    });

    // Add response interceptor to handle 401 errors
    instance.interceptors.response.use(
      response => response,
      async (error) => {
        const originalRequest = error.config;
        // If the error is 401 and we haven't already retried
        if (error.response?.status === 401 && !originalRequest._retry) {

          // window.location.reload();
          originalRequest._retry = true;
          try {
            const { token } = await getIdTokenSilently();
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            // If token refresh fails, redirect to login or handle as needed
            // You might want to add your auth redirect logic here

            window.location.reload();
            return Promise.reject(refreshError);
          }
        }
        else if (error.response?.status === 400 && error.response?.data?.duplicateProjectName === true) {
          toast.error('This project name is already in use. Please choose a different name.', {
            autoClose: 5000, // optional
            closeOnClick: true,
          });
          //updateError({isError: true, message: 'This project name is already in use for the chosen country. Please choose a different name.'})
          // window.setTimeout(() => {
          //     updateError({ isError: false, message: '' });
          //   }, 0);

        }

        return Promise.reject(error);
      }
    );


    return instance;
  }, [idToken, authData.idToken]);

  // Projects API
  const getAllProjects = async (
    page = 1,
    perPage = 12,
    issaved?: number,
    advancedFilters: Record<string, string> = {}
  ) => {
    try {
      const params = new URLSearchParams();

      if (advancedFilters.country) params.append("country_key", advancedFilters.country);
      if (advancedFilters.category) params.append("category_Key", advancedFilters.category);
      if (advancedFilters.subCategory) params.append("subcategory_key", advancedFilters.subCategory);
      if (advancedFilters.brand) params.append("brand_key", advancedFilters.brand);
      if (advancedFilters.search) params.append("project_name", advancedFilters.search);
      if (advancedFilters.projectStatus) params.append("project_statuskey", advancedFilters.projectStatus);
      if (advancedFilters.bu) params.append("buid", advancedFilters.bu);
      if (advancedFilters.scLead) params.append("sclead_key", advancedFilters.scLead);
      if (advancedFilters.risk) params.append("risk_key", advancedFilters.risk);
      if (advancedFilters.bigBet) params.append("big_bet", advancedFilters.bigBet);
      if (advancedFilters.launchYears) params.append("Launch_Year", advancedFilters.launchYears);

      params.append("page", String(page));
      params.append("limit", String(perPage));
      if (issaved !== undefined) params.append("issaved", String(issaved));

      const url = `/projects?${params.toString()}`;
      const response = await apiClient.get<APIResponse>(url);
      return response.data;
    } catch (error) {
      return handleError(error, 'fetching projects');
    }
  };

  const getProjectCounts = async (): Promise<ProjectCounts | null> => {
    try {
      const response = await apiClient.get<ProjectCounts>('/projects/counts');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching project counts');
    }
  };

  const getHCGlobalFilters = async (
    filters: HCGlobalFilters
  ): Promise<HCGlobalFiltersResponse | null> => {
    try {
      const response = await apiClient.post<HCGlobalFiltersResponse>(
        '/getHCGlobalFilters',
        filters
      );
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching HC global filters');
    }
  };

  const getDropdownOptions = async (filterType: any) => {
    try {
      const response = await apiClient.get(`/filters/${filterType}`);
      return response.data || [];
    } catch (error) {
      return handleError(error, `fetching ${filterType} options`);
    }
  };

  const getSclead = async () => {
    try {
      const response = await apiClient.get('/sclead');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching sclead');
    }
  };

  const getRisks = async () => {
    try {
      const response = await apiClient.get('/risks');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching risks');
    }
  };

  const getRequiredField = async (key: any) => {
    try {
      const response = await apiClient.get(`/projects/validateProject/${key}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching required fields');
    }
  };

  const getBigbets = async () => {
    try {
      const response = await apiClient.get('/bigbets');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching bigbets');
    }
  };

  const getProjectStatus = async () => {
    try {
      const response = await apiClient.get('/projectstatus');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching project status');
    }
  };

  const getLaunchYears = async () => {
    try {
      const response = await apiClient.get('/launchYears');
      return Array.isArray(response.data.launchYears) ? response.data.launchYears : [];
    } catch (error) {
      return handleError(error, 'fetching launch years');
    }
  };

  const getLaunchQuarter = async () => {
    try {
      const response = await apiClient.get('/launchQuarter');
      return Array.isArray(response.data.data) ? response.data.data : [];
    } catch (error) {
      return handleError(error, 'fetching launch quarters');
    }
  };

  const getLaunchMonth = async () => {
    try {
      const response = await apiClient.get('/getLaunchMonth');
      return Array.isArray(response.data.data) ? response.data.data : [];
    } catch (error) {
      return handleError(error, 'fetching launch months');
    }
  };

  const getLaunchWeek = async () => {
    try {
      const response = await apiClient.get('/getLaunchWeek');
      return Array.isArray(response.data.data) ? response.data.data : [];
    } catch (error) {
      return handleError(error, 'fetching launch weeks');
    }
  };

  const getFormat = async () => {
    try {
      const response = await apiClient.get('/lookup/BV_Format');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return handleError(error, 'fetching formats');
    }
  };

  const getCountries = async () => {
    try {
      const response = await apiClient.get('/countries');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return handleError(error, 'fetching countries');
    }
  };

  const uploadImage = async (formdata: FormData) => {
    try {
      const response = await apiClient.post('/projects/uploadImage', formdata, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (err) {
      return handleError(err, 'uploading image');
    }
  };

  const uploadProjectFile = async (projectKey: any, moduleId: any, formdata: FormData) => {
    try {
      const response = await apiClient.post(`/projects/file/${projectKey}/${moduleId}`, formdata, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (err) {
      return handleError(err, 'uploading file');
    }
  };

  const getProjectFiles = async (projectKey: any, moduleId: any, page: number, limit: number) => {
    try {
      const response = await apiClient.get(`/projects/files/${projectKey}/${moduleId}?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      return handleError(error, 'fetching Files');
    }
  };

  const deleteProjectFile = async (fileId: any) => {
    try {
      const response = await apiClient.delete(
        `/projects/file/${fileId}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'Failed to delete file');
    }
  }

  const createProject = async (payload: any) => {
    try {
      const response = await apiClient.post('/projects', payload);
      return response.data;
    } catch (err) {
      return handleError(err, 'creating project');
    }
  };

  const createV2Project = async (payload: any) => {
    try {
      const response = await apiClient.post('/projects/submit', payload);
      return response.data;
    } catch (err) {
      return handleError(err, 'creating project');
    }
  };

  const getProjectDetails = async (id: any) => {
    try {
      const response = await apiClient.get(`/projects/${id}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'Failed to fetch project details');
    }
  };
  const getProjectName = async (id: any) => {
    try {
      const response = await apiClient.get(`/projects/${id}`);
      if (!response.data) {
        return null;
      }
      return response?.data?.name;
    } catch (err) {
      return handleError(err, 'Failed to fetch project details');
    }
  };
  const removeProject = async (project_id: any) => {
    try {
      const response = await apiClient.delete(
        `/projects/${project_id}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'Failed to remove project');
    }
  };

  const getRemovedProjects = async (
    page = 1,
    perPage = 12,
  ) => {
    try {
      const params = new URLSearchParams();

      params.append("page", String(page));
      params.append("limit", String(perPage));
      const url = `/projects/softDeleted`;
      const response = await apiClient.get<APIResponse>(url);
      return response.data;
    } catch (error) {
      return handleError(error, 'Failed to fetch deleted projects');
    }
  };

  const deleteProject = async (project_id: any) => {
    try {
      const response = await apiClient.delete(
        `/projects/delete/${project_id}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'Failed to delete project');
    }
  };
  const restoreProject = async (project_id: any) => {
    try {
      const response = await apiClient.post(
        `/projects/restore/${project_id}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'Failed to restore project');
    }
  };

  /*******************************BC INFORMATION******************************************************* */
  const createBcInformation = async (payload: any) => {
    try {
      const response = await apiClient.post('/bcInformation', payload);
      return response.data;
    } catch (err) {
      return handleError(err, 'creating BC information');
    }
  };

  const getBcInformationDetails = async (id: any) => {
    try {
      const response = await apiClient.get(`/bcInformation/${id}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching BC information');
    }
  };

  const getCapexStatus = async () => {
    try {
      const response = await apiClient.get('/lookup/BCI_Capex_Status');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching capex status options');
    }
  };

  const getCptStatus = async () => {
    try {
      const response = await apiClient.get('/lookup/BCI_CPT_Status');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching CPT Status options');
    }
  };

  const updatebBCInformation = async (payload: any, project_key: any) => {
    try {
      const response = await apiClient.put(
        `/bcInformation/${project_key}`,
        payload
      );
      return response.data;
    } catch (err) {
      return handleError(err, 'updating BC information');
    }
  };

  const getInnoflexData = async (key: any) => {
    try {
      const response = await apiClient.get(`/bcInformation/InnoflexAutopopulate?searchTerm=${key}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching Innoflex data');
    }
  };

  const publishProjectOnSave = async (projectKey: any) => {
    try {
      const response = await apiClient.post(`/projects/publish/${projectKey}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'publishing project');
    }
  };

  // Static options


  return {
    getAllProjects,
    getProjectCounts,
    getHCGlobalFilters,
    getDropdownOptions,
    getSclead,
    getRisks,
    getBigbets,
    getProjectStatus,
    getLaunchYears,
    getLaunchQuarter,
    getLaunchMonth,
    getLaunchWeek,
    getFormat,
    getCountries,
    uploadImage,
    uploadProjectFile,
    getProjectFiles,
    deleteProjectFile,
    createProject,
    createV2Project,
    getProjectDetails,
    getProjectName,
    createBcInformation,
    getBcInformationDetails,
    updatebBCInformation,
    getInnoflexData,
    getRequiredField,
    publishProjectOnSave,
    removeProject,
    deleteProject,
    getRemovedProjects,
    restoreProject,
    getCapexStatus,
    getCptStatus
  };
};